<script setup lang="ts">
import { PV } from "@/widgets";
import { isString } from "es-toolkit";
import type { FileUploadSelectEvent } from "primevue";
import FileUpload from "primevue/fileupload";
import { watchEffect } from "vue";
import UpdateIcon from "~icons/ic/baseline-edit";
import ResetIcon from "~icons/prime/times";
import { defineActions, defineState } from "../common";
import { FormGuide, FormHead, FormMessage } from "../internal";
import { type BaseProps, defaultProps } from "../props";

interface FileMetadata {
    name: string;
    type: string;
    size: number;
}

type ModelType = string;
interface PropsType extends BaseProps<ModelType> {
    placeholder?: string;
}

const props = withDefaults(defineProps<PropsType>(), {
    ...defaultProps<ModelType>(),
});

const model = defineModel<ModelType | undefined>({
    default: undefined,
});

const state = defineState({
    model: model,
    props: props,
    hasValue: () => model.value !== undefined,
});

const actions = defineActions(model, props, state);

watchEffect(() => {
    if (model.value === "" || model.value === null) {
        model.value = undefined;
    }
});

function onFileSelect(event: FileUploadSelectEvent) {
    const file = event.files[0] as FileMetadata;
    const reader = new FileReader();

    reader.onload = async (e) => {
        if (e.target === null) return;
        if (!isString(e.target.result)) return;
        model.value = e.target.result;
    };

    reader.readAsDataURL(file as unknown as Blob);
}
</script>

<template>
    <div class="flex flex-col gap-1">
        <FormHead :actions="actions" :param="props" :state="state" />

        <template v-if="!state.editMode">
            <div class="relative h-full rns-g-static-input">
                <div class="flex flex-col h-full min-h-50 max-h-100 items-center justify-center p-4">
                    <div v-if="!state.hasValue" class="text-xl text-gray-800">
                        {{ props.placeholder ?? props.label }}
                    </div>
                    <PV.Image
                        v-else
                        class="w-full h-full flex flex-row items-center justify-center"
                        imageClass="max-h-[400px] p-8"
                        preview
                        :src="model"
                    />
                </div>
                <div
                    v-if="state.updatable"
                    class="absolute top-0 left-0 right-0 bottom-0 hover:bg-black hover:cursor-pointer hover:opacity-60 opacity-0"
                >
                    <div class="flex flex-col h-full items-center justify-center">
                        <div v-tooltip.top="'Ubah'">
                            <UpdateIcon class="text-gray-400 text-[3rem]" @click="actions.onUpdate" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-else>
            <div class="relative h-full rns-upload-box">
                <div class="flex flex-col h-full max-h-100 items-center justify-center">
                    <FileUpload
                        v-if="!state.hasValue"
                        accept="image/*"
                        auto
                        :chooseLabel="props.placeholder ?? 'Pilih Berkas'"
                        class="my-8"
                        customUpload
                        :maxFileSize="4 * 1024 * 1024"
                        mode="basic"
                        @select="onFileSelect"
                    />
                    <img v-else class="h-full max-h-100 py-8" :src="model" />
                </div>
                <div
                    v-if="state.hasValue && state.clearable"
                    class="absolute top-0 left-0 right-0 bottom-0 z-10 hover:bg-black hover:cursor-pointer hover:opacity-60 opacity-0 rounded-md"
                >
                    <div class="flex flex-col h-full items-center justify-center">
                        <div v-tooltip.top="'Reset'">
                            <ResetIcon class="text-gray-400 text-[4rem]" @click="actions.onReset" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <FormGuide :state="state" :text="props.guideText" />
        <FormMessage :state="state" />
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-upload-box {
    @apply border border-gray-300 hover:border-gray-400 shadow-xs rounded-md;
    @apply max-h-100 w-full flex flex-row items-center justify-center hover:cursor-pointer;
}
</style>
